import { z } from "zod";

import type { ApiClient } from "./client.ts";
import type { HouseholdId, UserId } from "@estate-platform/domain/ids";
import type { QueryClient } from "@tanstack/react-query";

const changeSchema = z.object({ kind: z.string().min(1) });

// WHY: which queries a change refreshes grows with every record kind and first paint never needs it; changes only arrive after SUBSCRIBED, which refetches everything anyway.
const loadRefresher = async () => {
  try {
    return await import("./realtime-refresh.ts");
  } catch {
    return null;
  }
};

const refreshOnChange = async (queryClient: QueryClient, kinds: readonly string[]): Promise<void> => {
  const refresher = await loadRefresher();
  if (refresher === null) {
    await queryClient.invalidateQueries();
    return;
  }
  for (const kind of kinds) {
    refresher.refreshAfter(queryClient, kind);
  }
};

const burstMs = 100;

// WHY: one write can change many rows and each row broadcasts its kind; a burst refreshes each kind once, where every message would cancel and restart the same refetches.
export const batchKinds = (flush: (kinds: readonly string[]) => void): ((kind: string) => void) => {
  let pending = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (kind) => {
    pending.add(kind);
    timer ??= setTimeout(() => {
      const kinds = [...pending];
      pending = new Set();
      timer = undefined;
      flush(kinds);
    }, burstMs);
  };
};

export interface Live {
  readonly watch: (householdId: HouseholdId, userId: UserId) => void;
  readonly stop: () => void;
}

interface Watched {
  readonly householdId: HouseholdId;
  readonly userId: UserId;
  readonly stop: () => void;
}

// WHY: household changes carry the kind that changed; the viewer's own topic carries new notifications, which only they may hear.
export const createLive = (client: Pick<ApiClient, "realtime">, queryClient: QueryClient): Live => {
  let watched: Watched | null = null;
  const changed = batchKinds((kinds) => {
    void refreshOnChange(queryClient, kinds);
  });
  const onChange = (payload: unknown): void => {
    const change = changeSchema.safeParse(payload);
    if (change.success) {
      changed(change.data.kind);
    } else {
      void queryClient.invalidateQueries();
    }
  };
  const onSubscribed = (): void => {
    void queryClient.invalidateQueries();
  };

  // WHY: the socket loads after first paint; a watch stopped before it arrives never opens its channels.
  const connect = (topics: readonly string[]): (() => void) => {
    let isStopped = false;
    let close = (): void => {
      isStopped = true;
    };
    const open = async (): Promise<void> => {
      const realtime = await client.realtime();
      if (isStopped || realtime === null) {
        return;
      }
      const closers = topics.map((topic) => realtime.open(topic, onChange, onSubscribed));
      close = () => {
        for (const closeChannel of closers) {
          closeChannel();
        }
      };
    };
    void open();
    return () => {
      close();
    };
  };

  const stop = (): void => {
    watched?.stop();
    watched = null;
  };

  return {
    watch: (householdId, userId) => {
      if (watched?.householdId === householdId && watched.userId === userId) {
        return;
      }
      stop();
      watched = { householdId, userId, stop: connect([`household:${householdId}`, `user:${userId}`]) };
    },
    stop,
  };
};
